import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, AlertCircle, Lock, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { shopSettingsService } from '../services/shopSettingsService';
import { loadBpiRecord, saveBpiRecord } from '../services/bpiService';
import { resolveForConfig } from '../services/bpiMetricsService';
import { barberDataService } from '../services/barberDataService';
import { appointmentService } from '../services/appointmentService';
import { computeBpi, mergeBpiConfig, bpiRank, type BpiConfig, type BpiParam } from '../shared/bpi';

/**
 * BPI Management (rework, Jose 2026-09-27). Owner-only. Computed params are READ-ONLY real values
 * (no more sliding money numbers); manual params are owner sliders. Transparent score math + a
 * required reason on any ± adjustment. Notes are captured for the AI (goals/growth). The parameter
 * set / weights / on-off live in Settings → Growth & Bonus; this screen sets the per-barber values.
 */
interface BPIManagementModalProps {
  barberId: string;
  barberName: string;
  isOwner: boolean;
  onClose: () => void;
  onSave: () => void;
}

const CATEGORY_ORDER = ['Core Performance', 'Service Quality', 'Professional Standards', 'Growth & Development', 'Client Relations'];

const BPIManagementModal: React.FC<BPIManagementModalProps> = ({ barberId, barberName, isOwner, onClose, onSave }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<BpiConfig | null>(null);
  const [computed, setComputed] = useState<Record<string, number | null>>({});
  const [manualValues, setManualValues] = useState<Record<string, number>>({});
  const [adjustment, setAdjustment] = useState(0);
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [notes, setNotes] = useState('');
  const [showMath, setShowMath] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const cfg = mergeBpiConfig(await shopSettingsService.getSettingsByType('bpiConfig'));
        const barber = barberDataService.getBarberById(barberId) as any;
        const appts = appointmentService.getAppointmentsByBarber(barberId);
        const resolved = barber ? resolveForConfig(cfg, barber, appts) : {};
        const rec = await loadBpiRecord(barberId);
        // seed manual values: saved → default → 60
        const seededManual: Record<string, number> = {};
        for (const p of cfg.params) {
          if (p.kind === 'manual') {
            const v = rec?.manualValues?.[p.key];
            seededManual[p.key] = typeof v === 'number' ? v : (typeof p.manualDefault === 'number' ? p.manualDefault : 60);
          }
        }
        setConfig(cfg);
        setComputed(resolved);
        setManualValues(seededManual);
        setAdjustment(rec?.adjustment || 0);
        setAdjustmentReason(rec?.adjustmentReason || '');
        setNotes(rec?.notes || '');
      } catch (e) {
        console.error('Error loading BPI:', e);
        toast.error('Failed to load BPI data');
      } finally {
        setLoading(false);
      }
    })();
  }, [barberId]);

  const result = useMemo(() => {
    if (!config) return null;
    return computeBpi(config, computed, manualValues, adjustment);
  }, [config, computed, manualValues, adjustment]);

  const rank = result ? bpiRank(result.final) : null;
  const adjMax = config?.adjustmentMax ?? 5;
  const reasonMissing = adjustment !== 0 && adjustmentReason.trim() === '';

  const setManual = (key: string, v: number) => setManualValues(prev => ({ ...prev, [key]: Math.max(0, Math.min(100, v)) }));

  const handleSave = async () => {
    if (!isOwner || !result) return;
    if (reasonMissing) { toast.error('A reason is required for a manual adjustment.'); return; }
    setSaving(true);
    try {
      await saveBpiRecord({
        barberId, manualValues, adjustment,
        adjustmentReason: adjustmentReason.trim(), notes: notes.trim(), finalScore: result.final,
      }, 'owner');
      toast.success('BPI saved.');
      onSave();
      onClose();
    } catch (e) {
      console.error('Error saving BPI:', e);
      toast.error('Failed to save BPI');
    } finally {
      setSaving(false);
    }
  };

  const valueLabel = (p: BpiParam): { text: string; muted?: boolean } => {
    if (p.kind === 'computed') {
      const v = computed[p.key];
      if (v === null || v === undefined) {
        if (p.source === 'rating') return { text: 'awaiting reviews', muted: true };
        if (p.source === 'serviceSpeed') return { text: 'no timed checkouts yet', muted: true };
        return { text: 'no data yet', muted: true };
      }
      return { text: `${Math.round(v)}%` };
    }
    return { text: `${Math.round(manualValues[p.key] ?? 0)}%` };
  };

  if (loading || !config || !result) {
    return (
      <div className="fixed inset-0 bg-black/60 z-[9998]" style={{ backdropFilter: 'blur(4px)' }} onClick={onClose}>
        <div className="absolute top-0 right-0 h-[100dvh] w-full max-w-lg bg-gray-800 shadow-2xl flex flex-col items-center justify-center" onClick={e => e.stopPropagation()}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
          <p className="text-gray-300 mt-4">Loading BPI…</p>
        </div>
      </div>
    );
  }

  const byCategory = CATEGORY_ORDER.map(cat => ({
    cat,
    params: config.params.filter(p => p.category === cat && p.track),
  })).filter(g => g.params.length > 0);

  return (
    <div className="fixed inset-0 bg-black/60 z-[9998]" style={{ backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div className="absolute top-0 right-0 h-[100dvh] w-full max-w-lg bg-gray-800 shadow-2xl flex flex-col" style={{ animation: 'slideInRight 0.3s ease-out' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Barber Performance Index</h2>
              <p className="text-white/80">{barberName}</p>
            </div>
            <button onClick={onClose} className="text-white hover:bg-white/20 rounded-lg p-2"><X className="h-6 w-6" /></button>
          </div>
          <div className="mt-4 bg-white/10 backdrop-blur rounded-lg p-4 flex items-end justify-between">
            <div>
              <p className="text-white/80 text-sm">BPI Score</p>
              <p className="text-4xl font-bold text-white">{result.final.toFixed(0)}<span className="text-xl text-white/60"> / 100</span></p>
            </div>
            {rank && <span className={`px-3 py-1 rounded-full bg-white/15 text-white font-semibold`}>{rank.label}</span>}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto scroll-fix p-6 space-y-5">
          {!isOwner && (
            <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 flex items-center gap-2 text-yellow-200 text-sm">
              <Lock className="h-4 w-4" /> View only — BPI is owner-editable.
            </div>
          )}

          {/* Transparent score math */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <button onClick={() => setShowMath(s => !s)} className="w-full flex items-center justify-between text-left">
              <span className="font-semibold text-purple-300 flex items-center gap-2"><Sparkles className="h-4 w-4" /> How this score is calculated</span>
              {showMath ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
            </button>
            {showMath && (
              <div className="mt-3 space-y-1.5 text-sm">
                {result.contributions.map(c => (
                  <div key={c.key} className={`flex items-center justify-between ${c.available ? 'text-gray-200' : 'text-gray-500 line-through'}`}>
                    <span>{c.label}</span>
                    <span className="tabular-nums text-gray-400">{c.value} × {c.weight}% = <span className="text-white font-medium">{c.points.toFixed(1)}</span></span>
                  </div>
                ))}
                <div className="border-t border-gray-600 mt-2 pt-2 flex items-center justify-between text-gray-200">
                  <span>Base score</span><span className="font-semibold text-white tabular-nums">{result.base.toFixed(1)}</span>
                </div>
                {result.adjustment !== 0 && (
                  <div className="flex items-center justify-between text-gray-200">
                    <span>Manual adjustment</span><span className={`font-semibold tabular-nums ${result.adjustment > 0 ? 'text-green-400' : 'text-red-400'}`}>{result.adjustment > 0 ? '+' : ''}{result.adjustment}</span>
                  </div>
                )}
                <div className="flex items-center justify-between text-white font-bold">
                  <span>Final</span><span className="tabular-nums">{result.final.toFixed(1)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Parameters by category */}
          {byCategory.map(({ cat, params }) => (
            <div key={cat} className="bg-gray-700/50 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-purple-300 uppercase tracking-wide mb-3">{cat}</h3>
              <div className="space-y-4">
                {params.map(p => {
                  const vl = valueLabel(p);
                  const scored = p.affectsScore && p.weight > 0;
                  const isComputed = p.kind === 'computed';
                  return (
                    <div key={p.key}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-100 font-medium text-sm">{p.label}</span>
                            {isComputed
                              ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 flex items-center gap-0.5"><Lock className="h-2.5 w-2.5" /> auto</span>
                              : <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">manual</span>}
                            {!scored && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-500/20 text-gray-400">tracked only</span>}
                          </div>
                          <p className="text-gray-400 text-xs mt-0.5">{p.description}</p>
                        </div>
                        <span className={`text-sm shrink-0 ${vl.muted ? 'text-gray-500 italic' : 'text-gray-200 font-semibold'}`}>{vl.text}</span>
                      </div>
                      {/* Bar / slider */}
                      {isComputed ? (
                        <div className="mt-2 h-2 bg-gray-600 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${vl.muted ? 0 : Math.round(computed[p.key] as number)}%` }} />
                        </div>
                      ) : (
                        <input
                          type="range" min={0} max={100} value={Math.round(manualValues[p.key] ?? 0)}
                          onChange={e => setManual(p.key, parseInt(e.target.value))}
                          disabled={!isOwner}
                          className="mt-2 w-full h-2 rounded-lg appearance-none cursor-pointer disabled:opacity-50"
                          style={{ background: `linear-gradient(to right, rgb(168,85,247) ${manualValues[p.key] ?? 0}%, rgb(75,85,99) ${manualValues[p.key] ?? 0}%)` }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Manual adjustment — transparent + required reason */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <h3 className="font-semibold text-blue-300 mb-1">Manual adjustment</h3>
            <p className="text-gray-400 text-xs mb-3">A bounded ± nudge (±{adjMax}) for something the metrics can't see. A reason is required and is fed to the coaching AI.</p>
            <div className="flex items-center gap-3">
              <input
                type="number" min={-adjMax} max={adjMax} step={0.5} value={adjustment}
                onChange={e => setAdjustment(Math.max(-adjMax, Math.min(adjMax, parseFloat(e.target.value) || 0)))}
                disabled={!isOwner}
                className="w-24 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white disabled:opacity-50"
              />
              <span className="text-gray-400 text-sm">points → final {result.final.toFixed(1)}</span>
            </div>
            <label className="block text-gray-200 text-sm mt-3 mb-1">
              Reason {adjustment !== 0 && <span className="text-red-400">*</span>}
            </label>
            <textarea
              value={adjustmentReason} onChange={e => setAdjustmentReason(e.target.value)} rows={2}
              disabled={!isOwner}
              className={`w-full px-3 py-2 bg-gray-600 border rounded-lg text-white disabled:opacity-50 ${reasonMissing ? 'border-red-500' : 'border-gray-500'}`}
              placeholder="Why is this adjustment being made?"
            />
            {reasonMissing && <p className="text-red-400 text-xs mt-1 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> A reason is required for any adjustment.</p>}
          </div>

          {/* Notes → AI */}
          <div className="bg-gray-700/50 rounded-lg p-4">
            <label className="block text-gray-200 text-sm mb-1">Coaching notes <span className="text-gray-500 text-xs">(fed to the AI → goals &amp; growth)</span></label>
            <textarea
              value={notes} onChange={e => setNotes(e.target.value)} rows={3} disabled={!isOwner}
              className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white disabled:opacity-50"
              placeholder="Anything the numbers don't capture…"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-gray-700 p-4 bg-gray-800 pb-safe flex items-center justify-between">
          <button onClick={onClose} className="px-6 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg">Cancel</button>
          {isOwner && (
            <button
              onClick={handleSave} disabled={saving || reasonMissing}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="h-5 w-5" /> {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BPIManagementModal;
